import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Button from '../ui/Button';

export default function CartDrawer() {
  const { cart, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, subtotal } = useCart();

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-navy/60 backdrop-blur-sm z-[60]"
          />
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-cream z-[70] shadow-2xl flex flex-col"
          >
            <div className="p-6 border-b border-brass/10 flex justify-between items-center bg-white">
              <h2 className="font-serif text-2xl text-navy">Workshop Bag</h2>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-creamDark rounded-full transition-colors"
              >
                <SafeIcon icon={FiIcons.FiX} className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 space-y-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-16 h-16 bg-brass/10 rounded-full flex items-center justify-center text-saddle">
                    <SafeIcon icon={FiIcons.FiShoppingBag} className="w-8 h-8" />
                  </div>
                  <p className="font-serif text-xl text-navy">Your bag is empty</p>
                  <p className="font-sans text-xs uppercase tracking-widest text-navy/40">Start your journey in the shop</p>
                  <Button variant="secondary" onClick={() => setIsCartOpen(false)}>Continue Browsing</Button>
                </div>
              ) : (
                cart.map((item) => (
                  <div key={item.id} className="flex gap-4 group">
                    <div className="w-24 h-24 bg-creamDark overflow-hidden flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-grow">
                      <div className="flex justify-between items-start">
                        <h3 className="font-serif text-navy leading-tight">{item.name}</h3>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="text-navy/30 hover:text-red-600 transition-colors"
                        >
                          <SafeIcon icon={FiIcons.FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="font-sans text-[10px] uppercase tracking-widest text-saddle mt-1">{item.category}</p>
                      <div className="flex justify-between items-center mt-4">
                        <div className="flex items-center border border-brass/20">
                          <button onClick={() => updateQuantity(item.id, -1)} className="px-3 py-1 hover:bg-creamDark transition-colors">-</button>
                          <span className="px-3 py-1 font-sans text-xs border-x border-brass/20">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, 1)} className="px-3 py-1 hover:bg-creamDark transition-colors">+</button>
                        </div>
                        <span className="font-sans font-bold text-navy">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 bg-white border-t border-brass/10 space-y-4">
                <div className="flex justify-between font-sans text-xs tracking-widest uppercase text-navy/60">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-serif text-2xl text-navy">
                  <span>Total</span>
                  <span className="text-saddle">${subtotal.toFixed(2)}</span>
                </div>
                <Button variant="accent" className="w-full py-5 text-[11px] font-bold" onClick={() => { setIsCartOpen(false); window.location.hash = '#/checkout'; }}>
                  Proceed to Checkout
                </Button>
                <p className="text-[9px] font-sans text-center text-navy/40 uppercase tracking-[0.2em]">
                  Shipping & taxes calculated at checkout
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}